import { useState, useEffect, useRef } from 'react';
// import AudiForm from '../../../components/AudiForm';
import { Formik, Form } from 'formik';
import cn from 'classnames';
import TextField from '../../../audi-ui-components/TextField';
import TextArea from '../../../audi-ui-components/TextArea';
import Checkbox from '../../../audi-ui-components/Checkbox';
import Button from '../../../audi-ui-components/Button';
import Spinner from '../../../audi-ui-components/Spinner';
import CheckboxGroup from '../../../components/CheckboxGroupField';
import DealerByPostcodeModule from '../../../components/DealerByPostcodeModule';
import IconSelect from '../../../audi-ui-components/icons/Select';
import PrivacyStatement from './PrivacyStatement';
import { validate, defaultSchema } from '../../../functions/validation';
import { formatMobile, formatDollar } from '../../../functions/textFormatting';
import { prefCentreService } from '../../../functions/apiRequestHelper';
import { gtmPush } from '../../../functions/gtm';
import API from '../../../functions/api';
import {
  PREF_CONTACT_OPTIONS,
  LABEL_OPTIN
} from '../../../constants';

const EnquiryForm = ({
  queryString,
  data = {},
  thankyouDisclaimer,
  locPostcode,
  show,
  onCancel,
  repaymentSymbol = "#",
  trackingPrefix,
  trackingFormName,
  theme,
}) => {

  const [serverError, setServerError] = useState(null);
  const [isFetchingUser, setIsFetchingUser] = useState(true);
  const [user, setUser] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [height, setHeight] = useState(0);
  const [trackedFields, setTrackedFields] = useState({});
  const formRef = useRef(null);

  const auiTheme = theme === "dark" ? "light" : null;

  useEffect(() => {
    if (queryString.sfid) {
      prefCentreService.get(queryString.sfid).then((result) => {
        setUser(result);
        setIsFetchingUser(false);
      }).catch(() => {
        setIsFetchingUser(false);
      });
    } else {
      setIsFetchingUser(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      setHeight(formRef.current?.getBoundingClientRect().height + 30);
      setTimeout(()=>{ setHeight('auto'); }, 2000);
    } else {
      setHeight(0);
      // setTrackedFields([]);
    }
  }, [show]);

  const submitForm = (values, formikProps) => {
    let {dealer, ..._values} = values;
    let payload = {...data, ..._values};
    if (process.env.RAZZLE_APP_ENV !== "local" || payload.submitToBackend) {
      return API({
        url: `/1/afs/sendEmailNotification`,
        method: "POST",
        data: payload
      }).then((result) => { // SUCCESS
        setSubmittedData(values);
        setSubmitted(true);
        gtmPush("form", "formSubmitSuccess", trackingFormName);
      }).catch((error) => { // FAIL
        if (error.response?.data?.modelState) {
          let errors = {};
          for (let key in error.response.data.modelState) {
            if (!Object.hasOwnProperty(key)) {
              errors[key] = error.response.data.modelState[key][0];
            }
          }
          formikProps.setErrors(errors);
        } else {
          let msg = "An error has occured";
          if (error.response?.data?.message) {
            msg = error.response.data.message;
          }
          setServerError(msg);
        }
        formikProps.setSubmitting(false);
        scrollToError();
      });
    } else {
      // fake submit for frontend testing
      console.log(payload);
      setSubmitted(true);
      setSubmittedData(values);
    }
  }

  const validateForm = (values) => {
    var _schema = {
      firstName: defaultSchema.firstName,
      lastName: defaultSchema.lastName,
      email: defaultSchema.emailAddress,
      phone: defaultSchema.mobilePhone,
      preferredContactMethod: defaultSchema.preferredContactMethod,
      dealerName: {presence: {
        message: "Please select a dealer",
        allowEmpty: false
      }}
    };
    var v = validate(values, _schema, {format: "firstError", fullMessages: false});
    // console.log("validation", values, v);
    return v;
  }

  const scrollToError = () => {
    setTimeout(() => {
      let el = document.querySelector(".is-invalid");
      if (el) {
        el.scrollIntoView({behavior: "smooth", block: "center"});
      }
    }, 1000);
  }

  return (
    <div className={cn("finance-enquiry")} style={{height}}>
    <div ref={formRef}>

      <p className="aui-headline-3 mb-3 pt-3">Send an enquiry</p>

      {submitted && submittedData !== null && <>
        <p className="aui-headline-3 mb-4 text-center">Thank you</p>
        <p className="mb-3 text-center">We will send you an email with your request.</p>
        <p className="mb-3 text-center">
            {submittedData.dealerName ? <>A team member from <b>{submittedData.dealerName}</b></> : 'We'}
            {' '}will be in touch regarding your enquiry.
        </p>
        <div className={`${theme === "dark" ? "aui-color-gray10" : "aui-color-gray95"} p-3 mb-5`}>
          <p className="aui-font-extended mb-2"><b>Car Requested</b></p>
          <div className="text-center">
            {data?.vehicleImageUrl && <img src={data.vehicleImageUrl} alt="" style={{maxWidth: "50%"}} />}
            <p>{data?.modelName}</p>
          </div>
          <hr className="my-3" />
          <p className="aui-font-extended mb-2"><b>Finance</b></p>
          {data.loanType && <p>{data.loanType} loan</p>}
          <p>{formatDollar(data.repayment)} {data.repaymentLabel?.replace("#","")}<sup>{repaymentSymbol}</sup></p>
          <hr className="my-3" />
          <p className="aui-font-extended mb-2"><b>Dealer</b></p>
          <p>{submittedData.dealerName} {submittedData.dealer?.state ? `- ${submittedData.dealer.state}` : ""}</p>
          <p>{submittedData.dealer?.phone}</p>
          <hr className="my-3" />
          <p className="aui-font-extended mb-2"><b>Your Details</b></p>
          <p>{submittedData.firstName} {submittedData.lastName}</p>
          <p>{submittedData.email}</p>
          <p>{submittedData.phone}</p>
          <p className="mb-2">{submittedData.postcode}</p>
          {submittedData.tradeIn && <p><IconSelect small /> I'd like to discuss trade in options</p>}
        </div>
        {thankyouDisclaimer && <div className="aui-small pb-5">
          {thankyouDisclaimer}
        </div>}
      </>}
      
      {!isFetchingUser && !submitted && <Formik onSubmit={submitForm} initialValues={{
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user?.email || "",
        phone: user?.mobile?.indexOf('04') === 0 ? formatMobile(user.mobile) : "",
        preferredContactMethod: user?.preferredContactMethod || "Email",
        postcode: locPostcode
      }} validate={validateForm} enableReinitialize>
        {({values, errors, touched, setFieldValue, setFieldTouched, isSubmitting, submitCount}) => (
          <Form className="audi-form mb-5">
            {isSubmitting && <div className="spinner-wrapper sticky"><Spinner isContinuous /></div>}
            <TextField name="firstName"
              label="First name*"
              value={values.firstName}
              onChange={(v) => { setFieldValue('firstName', v); }}
              isInvalid={errors.firstName && touched.firstName}
              invalidMessage={errors.firstName}
              onBlur={() => { setFieldTouched('firstName'); }}
              onFocus={()=>{
                if (!trackedFields.firstName) {
                  gtmPush(`${trackingPrefix}FirstNameClicked`, trackingFormName);
                  setTrackedFields(Object.assign({}, trackedFields, {firstName: true}));
                }
              }}
              className="form-field"
              theme={auiTheme}
            />
            <TextField name="lastName"
              label="Last name*"
              value={values.lastName}
              onChange={(v) => { setFieldValue('lastName', v); }}
              isInvalid={errors.lastName && touched.lastName}
              invalidMessage={errors.lastName}
              onBlur={() => { setFieldTouched('lastName'); }}
              onFocus={()=>{
                if (!trackedFields.lastName) {
                  gtmPush(`${trackingPrefix}LastNameClicked`, trackingFormName);
                  setTrackedFields(Object.assign({}, trackedFields, {lastName: true}));
                }
              }}
              className="form-field"
              theme={auiTheme}
            />
            <TextField name="email"
              label="Email*"
              value={values.email}
              onChange={(v) => { setFieldValue('email', v.trim()); }}
              isInvalid={errors.email && touched.email}
              invalidMessage={errors.email}
              onBlur={() => { setFieldTouched('email'); }}
              onFocus={()=>{
                if (!trackedFields.email) {
                  gtmPush(`${trackingPrefix}EmailNameClicked`, trackingFormName);
                  setTrackedFields(Object.assign({}, trackedFields, {email: true}));
                }
              }}
              className="form-field"
              theme={auiTheme}
            />
            <TextField name="phone"
              label="Mobile number*"
              value={values.phone}
              onChange={(v) => { let _v = formatMobile(v); setFieldValue('phone', _v); }}
              isInvalid={errors.phone && touched.phone}
              invalidMessage={errors.phone}
              onBlur={() => { setFieldTouched('phone'); }}
              onFocus={()=>{
                if (!trackedFields.phone) {
                  gtmPush(`${trackingPrefix}MobileClicked`, trackingFormName);
                  setTrackedFields(Object.assign({}, trackedFields, {phone: true}));
                }
              }}
              className="form-field"
              theme={auiTheme}
            />
            <CheckboxGroup name="preferredContactMethod"
              label="Preferred method of contact *"
              options={PREF_CONTACT_OPTIONS}
              delimeter=";"
              value={values.preferredContactMethod}
              onChange={(v) => {
                setFieldValue('preferredContactMethod', v);
                if (!trackedFields.contact) {
                  gtmPush(`${trackingPrefix}PreferredMethodClicked`, trackingFormName);
                  setTrackedFields(Object.assign({}, trackedFields, {contact: true}));
                }
              }}
              isInvalid={errors.preferredContactMethod && touched.preferredContactMethod}
              invalidMessage={errors.preferredContactMethod}
              onBlur={() => { setFieldTouched('preferredContactMethod'); }}
              className="form-field"
              theme={auiTheme}
            />
            <DealerByPostcodeModule
              postcodeName="postcode"
              updateOnExtChange
              onSelectDealer={(d) => {
                if (!trackedFields.dealer) {
                  gtmPush(`${trackingPrefix}DealerSelected`, trackingFormName, d.dealer.name);
                  setTrackedFields(Object.assign({}, trackedFields, {dealer: true}));
                }
              }}
              theme={theme}
              values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} submitCount={submitCount}
            />
            <Checkbox name="tradeIn"
              label="I'd like to discuss trade in options."
              checked={!!values.tradeIn}
              onChange={(v,e) => {
                setFieldValue('tradeIn', e.target.checked);
                if (!trackedFields.tradeIn) {
                  gtmPush(`${trackingPrefix}TradeInClicked`, trackingFormName);
                  setTrackedFields(Object.assign({}, trackedFields, {tradeIn: true}));
                }
              }}
              isInvalid={errors.tradeIn && touched.tradeIn}
              invalidMessage={errors.tradeIn}
              onBlur={() => { setFieldTouched('tradeIn'); }}
              className="form-field"
              theme={auiTheme}
            />
            <div className={cn("collapsible-field", {"collapsed": !values.tradeIn})}>
              <TextArea name="tradeInDetail"
                label="Please provide details about your trade in vehicle"
                value={values.tradeInDetail}
                onChange={(v) => { setFieldValue('tradeInDetail', v); }}
                isInvalid={errors.tradeInDetail && touched.tradeInDetail}
                invalidMessage={errors.tradeInDetail}
                onBlur={() => { setFieldTouched('tradeInDetail'); }}
                className="form-field"
                theme={auiTheme}
              />
            </div>
            <Checkbox name="optIn"
              label={LABEL_OPTIN}
              checked={!!values.optIn}
              onChange={(v,e) => {
                setFieldValue('optIn', e.target.checked);
                if (!trackedFields.optIn) {
                  gtmPush(`${trackingPrefix}OptInClicked`, trackingFormName);
                  setTrackedFields(Object.assign({}, trackedFields, {optIn: true}));
                }
              }}
              isInvalid={errors.optIn && touched.optIn}
              invalidMessage={errors.optIn}
              onBlur={() => { setFieldTouched('optIn'); }}
              className="form-field"
              theme={auiTheme}
            />
            {process.env.RAZZLE_APP_ENV === 'local' && <div className="form-field dev-only">
              <Checkbox name="submitToBackend" label="Submit to backend" theme={auiTheme} checked={!!values.submitToBackend} value="" onChange={(v,e) => { setFieldValue('submitToBackend', e.target.checked); }} />
            </div>}
            <div className="form-field submit">
              {serverError && <p className="aui-color-text-red submit-error">{serverError}</p>}
              <Button type="submit" variant="primary" onClick={scrollToError} theme={auiTheme}>Submit</Button>
              <Button variant="secondary" className="ms-2" onClick={onCancel} theme={auiTheme}>Cancel</Button>
            </div>
            <PrivacyStatement />
          </Form>
        )}
      </Formik>}

    </div>
    </div>
  );
};

export default EnquiryForm;